import { useCallback, useMemo, useState, type CSSProperties } from 'react';

import { useGetBiomassDistribution } from '@restDataAccess';
import { FlexAligner } from '@scaleaq/scaleui';
import { BiomassDistributionRequest } from '@scaleaq/sitecontrol-lib';
import { Duration } from 'luxon';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  BiomassDistributionConfig,
  BiomassDistributionModal,
  BiomassDistributionView,
} from '@/components/biomassDistribution/';
import { getBiomassDistributionRequest } from '@/components/biomassDistribution/biomassDistribution.utils.ts';
import { BiomassDistributionChart } from '@/components/biomassDistribution/BiomassDistributionChart.tsx';
import { BiomassDistributionEmptyState } from '@/components/biomassDistribution/BiomassDistributionEmptyState.tsx';
import { BiomassDistributionTable } from '@/components/biomassDistribution/BiomassDistributionTable.tsx';
import { BiomassDistributionToolbar } from '@/components/biomassDistribution/BiomassDistributionToolbar.tsx';
import { Content } from '@/components/cardWithThreeFooterBox';
import { useOpenModal, usePeriodicCallback } from '@/hooks';
import { useLayoutProps } from '@/hooks/layoutHooks';
import { useBiomassDistributionPreferencesStorage } from '@/stores';
import { ComponentType, GenericWidgetProps } from '@/types';
import { Icon, responsive } from '@/utils';
import { BIOMASS_DISTRIBUTION_COMPONENT_NAME } from '@/widgetComponentNames';

import { GridWidget } from './GridWidget';

interface BiomassDistributionProps extends GenericWidgetProps {
  config?: BiomassDistributionConfig;
  editable?: boolean;
  widgetId: string;
  style?: CSSProperties;
}

export const BiomassDistribution: ComponentType<BiomassDistributionProps> = ({
  config: configInput,
  editable = true,
  widgetId,
  style,
  ...rest
}) => {
  const { t } = useTranslation(['orbitOne', 'common']);
  const modalController = useOpenModal();
  // Layout config now holds identity only (sensor). Display preferences live in a
  // session-scoped store keyed by widgetId, so the widget behaves the same whether it
  // reads its sensor from the layout store (landing page) or a `config` prop (OrbitOne).
  const {
    config: { sensorId: inventoryId, sensorName },
  } = useLayoutProps<BiomassDistributionConfig>({
    configInput,
    defaultConfig: { sensorId: '', sensorName: '' },
    widgetId,
  });

  const { interval, binSize, view } = useBiomassDistributionPreferencesStorage(widgetId);

  const [distributionRequest, setDistributionRequest] = useState<BiomassDistributionRequest>(() =>
    getBiomassDistributionRequest(interval, binSize)
  );

  const updateRequest = useCallback(() => {
    setDistributionRequest(getBiomassDistributionRequest(interval, binSize));
  }, [interval, binSize]);
  const updateInterval = useMemo(() => Duration.fromObject({ hours: 1 }), []);

  usePeriodicCallback(updateRequest, updateInterval, 'BiomassDistribution');

  const { data, isLoading, isError, isFetched, isRateLimited } = useGetBiomassDistribution(
    inventoryId,
    distributionRequest
  );

  const bins = useMemo(() => data?.bins ?? [], [data?.bins]);
  const totalCount = data?.totalCount ?? 0;
  const hasData = !isFetched || bins.length > 0;

  const { editId } = useMemo(() => ({ editId: uuidv4() }), []);

  const toolbarActionList = useMemo(
    () =>
      editable
        ? [
            {
              id: editId,
              label: t('common:edit'),
              onClick: modalController.handleOpenModal,
              Icon: Icon.SlidersHorizontal,
            },
          ]
        : [],
    [editId, editable, modalController.handleOpenModal, t]
  );

  return (
    <>
      <GridWidget
        {...rest}
        headerTitle={t('orbitOne:biomassDistribution.title', { sensorName })}
        id={widgetId}
        isError={isError}
        isLoading={isLoading}
        isRateLimited={isRateLimited}
        cloudBacked={true}
        style={style}
        toolbarActionList={toolbarActionList}
      >
        <Content style={{ paddingTop: '0', overflow: 'hidden' }}>
          <FlexAligner direction='column' gap={responsive.spacing.large} fullHeight>
            <BiomassDistributionToolbar widgetId={widgetId} />
            <div
              style={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                overflowY: 'auto',
              }}
            >
              {!hasData && <BiomassDistributionEmptyState />}
              {hasData &&
                (view === BiomassDistributionView.Table ? (
                  <BiomassDistributionTable bins={bins} totalCount={totalCount} binSize={binSize} />
                ) : (
                  <BiomassDistributionChart
                    widgetId={widgetId}
                    bins={bins}
                    totalCount={totalCount}
                    statistics={data?.statistics}
                    binSize={binSize}
                  />
                ))}
            </div>
          </FlexAligner>
        </Content>
      </GridWidget>
      <BiomassDistributionModal widgetId={widgetId} modalController={modalController} />
    </>
  );
};

// Static Property
BiomassDistribution.componentName = BIOMASS_DISTRIBUTION_COMPONENT_NAME;
